import { useCallback } from "react";
import { useFetch2 } from "../../../util";

export type CalendarAppointmentItem = {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  description: string | null;
  sendConfirmationMessage: boolean;
  channelConfirmationMessage: CalendarAppointmentConfirmationChannel | null;
};

export type CalendarAppointmentConfirmationChannel = "WHATSAPP";

export type CreateCalendarAppointmentInput = {
  name: string;
  calendarId: number;
  contactId: number | null;
  startDate: string;
  endDate: string;
  description: string | null;
  sendConfirmationMessage: boolean;
  channelConfirmationMessage: CalendarAppointmentConfirmationChannel | null;
  confirmationMessageDate: string | null;
};

export type CreateCalendarAppointmentResponse = {
  id: number;
};

export type GetCalendarAppointmentResponse = {
  id: number;
  name: string;
  calendarId: number;
  contactId: number | null;
  startDate: string;
  endDate: string;
  description: string | null;
  sendConfirmationMessage: boolean;
  channelConfirmationMessage: CalendarAppointmentConfirmationChannel | null;
  confirmationMessageDate: string | null;
};

export type UpdateCalendarAppointmentInput = {
  id: number;
  name: string;
  calendarId: number;
  contactId: number | null;
  startDate: string;
  endDate: string;
  description: string | null;
  sendConfirmationMessage: boolean;
  channelConfirmationMessage: CalendarAppointmentConfirmationChannel | null;
  confirmationMessageDate: string | null;
};

export type UpdateCalendarAppointmentResponse = {
  id: number;
};

export type DeleteCalendarAppointmentResponse = {
  id: number;
};

export type VisibleCalendarItem = {
  calendarId: number;
  name: string;
  owner: boolean;
  sharedWithUser: boolean;
};

export type CalendarDetailsItem = {
  id: number;
  name: string;
  confirmationMessage: string;
  breakAfterMinutes: number | null;
  botId: number | null;
  createdByName: string | null;
  createdAt: string | null;
  updatedByName: string | null;
  updatedAt: string | null;
};

export type GetInitialCalendarResponse = {
  currentMonth: number;
  currentYear: number;
  calendar: CalendarDetailsItem | null;
  appointments: CalendarAppointmentItem[];
  visibleCalendars: VisibleCalendarItem[];
};

export type GetCalendarViewResponse = {
  month: number;
  year: number;
  calendar: CalendarDetailsItem | null;
  appointments: CalendarAppointmentItem[];
};

export type SearchCalendarAppointmentsResponse = {
  appointments: SearchCalendarAppointmentItem[];
};

export type SearchCalendarAppointmentItem = {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  description: string | null;
  contactName: string | null;
  contactPhone: string | null;
};

export type CalendarEditSharedUserItem = {
  id: number;
  userId: number;
  userName: string | null;
  owner: boolean;
};

export type CalendarEditWorkDayPeriodItem = {
  id: number;
  calendarWorkDayId: number | null;
  startTime: string;
  endTime: string;
};

export type CalendarEditWorkDayItem = {
  id: number;
  calendarId: number;
  weekDay:
    | "SUNDAY"
    | "MONDAY"
    | "TUESDAY"
    | "WEDNESDAY"
    | "THURSDAY"
    | "FRIDAY"
    | "SATURDAY";
  periods: CalendarEditWorkDayPeriodItem[];
};

export type CalendarEditDayOffItem = {
  id: number;
  calendarId: number;
  date: string;
  description: string | null;
};

export type GetCalendarEditResponse = {
  id: number;
  name: string;
  confirmationMessage: string;
  breakAfterMinutes: number | null;
  botId: number | null;
  sharedUsers: CalendarEditSharedUserItem[];
  workDays: CalendarEditWorkDayItem[];
  dayOffs: CalendarEditDayOffItem[];
};

export type SaveCalendarEditInput = {
  id: number;
  name: string;
  confirmationMessage: string;
  breakAfterMinutes: number | null;
  botId: number | null;
  userIds: number[];
  workDays: {
    id?: number | null;
    calendarId?: number | null;
    weekDay: CalendarEditWorkDayItem["weekDay"];
    periods: {
      id?: number | null;
      calendarWorkDayId?: number | null;
      startTime: string;
      endTime: string;
    }[];
  }[];
  dayOffs: {
    id?: number | null;
    calendarId?: number | null;
    date: string;
    description: string | null;
  }[];
};

export type SaveCalendarEditResponse = {
  id: number;
};

export default function useCalendar() {
  const fetch = useFetch2();

  const getInitialCalendar = useCallback((companyId: number) => {
    return fetch<GetInitialCalendarResponse>(
      `/api/calendars/initial?companyId=${companyId}`,
      { method: "GET" }
    );
  }, [fetch]);

  const getCalendarView = useCallback((calendarId: number, month: number, year: number) => {
    return fetch<GetCalendarViewResponse>(
      `/api/calendars/view?calendarId=${calendarId}&month=${month}&year=${year}`,
      { method: "GET" }
    );
  }, [fetch]);

  const searchCalendarAppointments = useCallback(
    (calendarId: number, search: string, limit = 10) => {
      const query = new URLSearchParams({
        calendarId: String(calendarId),
        search,
        limit: String(limit)
      });

      return fetch<SearchCalendarAppointmentsResponse>(
        `/api/calendar-appointments/search?${query.toString()}`,
        { method: "GET" }
      );
    },
    [fetch]
  );

  const getCalendarEdit = useCallback((calendarId: number) => {
    return fetch<GetCalendarEditResponse>(`/api/calendars/${calendarId}/edit`, {
      method: "GET"
    });
  }, [fetch]);

  const saveCalendarEdit = useCallback((input: SaveCalendarEditInput) => {
    return fetch<SaveCalendarEditResponse>(`/api/calendars`, { method: "PATCH" }, input);
  }, [fetch]);

  const createCalendarAppointment = useCallback((input: CreateCalendarAppointmentInput) => {
    return fetch<CreateCalendarAppointmentResponse>(
      `/api/calendar-appointments`,
      { method: "POST" },
      input
    );
  }, [fetch]);

  const getCalendarAppointment = useCallback((id: number) => {
    return fetch<GetCalendarAppointmentResponse>(
      `/api/calendar-appointments/${id}`,
      { method: "GET" }
    );
  }, [fetch]);

  const updateCalendarAppointment = useCallback((input: UpdateCalendarAppointmentInput) => {
    return fetch<UpdateCalendarAppointmentResponse>(
      `/api/calendar-appointments`,
      { method: "PATCH" },
      input
    );
  }, [fetch]);

  const deleteCalendarAppointment = useCallback((id: number) => {
    return fetch<DeleteCalendarAppointmentResponse>(
      `/api/calendar-appointments/${id}`,
      { method: "DELETE" }
    );
  }, [fetch]);

  return {
    getInitialCalendar,
    getCalendarView,
    searchCalendarAppointments,
    getCalendarEdit,
    saveCalendarEdit,
    createCalendarAppointment,
    getCalendarAppointment,
    updateCalendarAppointment,
    deleteCalendarAppointment
  };
}
